import {
  createContext,
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from "react";
import { getFolderInfo } from "../../../services/api/files";
import { getJobNumbers } from "../../../services/api/core";

export const FilesContext = createContext(null);

/**
 * Read the current Files context value.
 *
 * @returns {Object}
 */
export function useFilesContext() {
  const context = useContext(FilesContext);
  if (!context) {
    throw new Error("useFilesContext must be used within a FilesProvider");
  }
  return context;
}

/**
 * Build Files domain state for FilesProvider / FilesRoutes.
 *
 * @returns {Object}
 */
export function useFilesCtx() {
  // Files-related state
  const [folderData, setFolderData] = useState(null);
  const [isGridView, setIsGridView] = useState(false);
  const [isDynamicView, setIsDynamicView] = useState(true);
  const [jobs, setJobs] = useState([]);

  // Queue to store API requests
  const requestQueue = useRef([]);
  const isProcessingQueue = useRef(false); // Tracks if the queue is currently being processed
  const requestRate = useRef(15); // Requests per second (can be adjusted)

  /**
   * Processes the request queue at the specified rate.
   * Executes requests in the order they are added to the queue.
   */
  const processQueue = useCallback(async () => {
    if (isProcessingQueue.current) return; // Prevent multiple processors from running
    isProcessingQueue.current = true;

    while (requestQueue.current.length > 0) {
      const { requestFn, resolve, reject } = requestQueue.current.shift(); // Get the next request
      try {
        const result = await requestFn(); // Execute the request
        resolve(result); // Resolve the promise with the result
      } catch (error) {
        reject(error); // Reject the promise if an error occurs
      }
      // Wait before processing the next request to maintain the rate limit
      await new Promise((resolve) =>
        setTimeout(resolve, 1000 / requestRate.current)
      );
    }

    isProcessingQueue.current = false; // Mark queue processing as complete
  }, []);

  /**
   * Adds a request to the queue.
   * High-priority requests are added to the front of the queue,
   * while low-priority requests are added to the back.
   *
   * @param {Function} requestFn - The function to execute the API request.
   * @param {string} priority - The priority of the request ("high" or "low").
   * @returns {Promise} - A promise that resolves or rejects based on the request result.
   */
  const enqueueRequest = useCallback(
    (requestFn, priority = "low") => {
      return new Promise((resolve, reject) => {
        const request = { requestFn, resolve, reject };
        if (priority === "high") {
          requestQueue.current.unshift(request); // Add to the front for high priority
        } else {
          requestQueue.current.push(request); // Add to the back for low priority
        }
        processQueue();
      });
    },
    [processQueue]
  );

  useEffect(() => {
    /**
     * Loads the Shared/Projects folder as a high-priority queued request.
     *
     * @returns {Promise<void>}
     */
    async function fetchFolderData() {
      try {
        const data = await enqueueRequest(
          () => getFolderInfo("Shared/Projects"),
          "high"
        );
        setFolderData(data);
      } catch (error) {
        console.error("Error fetching folder data:", error);
      }
    }
    fetchFolderData();
  }, [enqueueRequest]);

  // Fetch jobs for Upload functionality
  useEffect(() => {
    getJobNumbers()
      .then(setJobs)
      .catch((e) => console.error(`could not get jobs for week of `, e));
  }, []);

  const contextValue = useMemo(
    () => ({
      folderData,
      setFolderData,
      isGridView,
      setIsGridView,
      isDynamicView,
      setIsDynamicView,
      enqueueRequest,
      requestQueue,
      isProcessingQueue,
      // Upload-related values
      jobs,
    }),
    [
      folderData,
      setFolderData,
      isGridView,
      setIsGridView,
      isDynamicView,
      setIsDynamicView,
      enqueueRequest,
      isProcessingQueue,
      jobs,
    ]
  );

  return {
    ...contextValue,
  };
}

/**
 * Provide Files domain state (Egnyte folder data, jobs, request queue).
 *
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - Child tree that needs Files context.
 * @returns {JSX.Element}
 */
export function FilesProvider({ children }) {
  const value = useFilesCtx();
  return (
    <FilesContext.Provider value={value}>{children}</FilesContext.Provider>
  );
}

export default useFilesCtx; // Export the hook as default for backward compatibility
